import React, { useState } from 'react';
import {
    Alert,
    Divider,
    Form,
    Input,
    InputNumber as AntDInputNumber,
    Modal,
    Radio,
    message,
    type RadioChangeEvent,
} from 'antd';
import { connect } from 'react-redux';
import { useT } from 'hooks/Locale/useT';
import { UsersType } from 'components/UsersComponents/types';
import useQueryApiClient from 'utils/useApiClient';
import { Button, Tag, Title, TranslatedMessage } from 'ui';
import { TagStatus } from 'ui/Tag/types';
import moment from 'moment';
import { config } from 'config/config';
import CopyValue from 'ui/CopyValue';

interface OTPModalInterface {
    visible: boolean;
    onCancel: React.Dispatch<React.SetStateAction<boolean>>;
    setFilter: React.Dispatch<React.SetStateAction<string>>;
    reload: React.Dispatch<React.SetStateAction<boolean>>;
    user: UsersType | null;
    session?: unknown;
}

type ReduxState = {
    session: unknown;
};

type LastActiveOtpInfo = {
    generated_at: string | null;
    expires_at: string | null;
    used_at: string | null;
    attempts_count: number | null;
    created_by: string | null;
    password: string | null;
};

type SendChannel = 'EMAIL' | 'SMS';
type OrganizationType = string;

type SendOtpPayload = {
    channel: SendChannel;
    destination?: string;
    organizationId?: number;
    organizationName?: string;
    organizationType?: OrganizationType;
    expiresMinutes?: number;
};

const mapStateToProps = (state: ReduxState) => {
    return {
        session: state.session,
    };
};

const OTPModal: React.FC<OTPModalInterface> = (props) => {
    const { visible, onCancel, user } = props;
    const [form] = Form.useForm();
    const [lastActiveOtp, setLastActiveOtp] = useState<LastActiveOtpInfo | null>(null);
    const t = useT();
    const [sendChannel, setSendChannel] = useState<SendChannel>('EMAIL');
    const [sendDestination, setSendDestination] = useState<string>('');
    const [sendCooldownUntilMs, setSendCooldownUntilMs] = useState<number>(0);
    const [nowMs, setNowMs] = useState<number>(() => Date.now());

    React.useEffect((): void => {
        setSendCooldownUntilMs(0);
    }, [user?.uuid]);

    const handleCancel = (): void => {
        onCancel(false);
    };

    const updateLastActiveOtpFromResponse = (response: unknown): void => {
        if (!response || typeof response !== 'object' || Array.isArray(response)) {
            setLastActiveOtp(null);
            return;
        }

        const data = response as Record<string, unknown>;
        const attemptsRaw = data.attempts_count;
        let attemptsCount: number | null = null;

        if (typeof attemptsRaw === 'number') {
            attemptsCount = attemptsRaw;
        } else if (typeof attemptsRaw === 'string' && attemptsRaw.trim() !== '') {
            const parsed = Number(attemptsRaw);
            attemptsCount = Number.isNaN(parsed) ? null : parsed;
        }

        setLastActiveOtp({
            generated_at: typeof data.generated_at === 'string' ? data.generated_at : null,
            expires_at: typeof data.expires_at === 'string' ? data.expires_at : null,
            used_at: typeof data.used_at === 'string' ? data.used_at : null,
            attempts_count: attemptsCount,
            created_by: typeof data.created_by === 'string' ? data.created_by : null,
            password: typeof data.password === 'string' ? data.password : null,
        });
    };

    const { fetch } = useQueryApiClient({
        request: {
            disableOnMount: true,
            url: '/api/v1/auth/otp/' + user?.uuid,
        },
        onSuccess: (response: unknown): void => {
            updateLastActiveOtpFromResponse(response);
        },
    });

    const fetchRef = React.useRef(fetch);
    React.useEffect((): void => {
        fetchRef.current = fetch;
    }, [fetch]);

    const lastFetchedUuidRef = React.useRef<string | null>(null);

    const userRecord = user as unknown as Record<string, unknown> | null;
    const userOrganizationId =
        userRecord && typeof userRecord.organizationId === 'number'
            ? userRecord.organizationId
            : undefined;
    const userOrganizationName =
        userRecord && typeof userRecord.organizationName === 'string'
            ? userRecord.organizationName
            : undefined;
    const userOrganizationType =
        userRecord && typeof userRecord.organizationType === 'string'
            ? userRecord.organizationType
            : undefined;

    const getDefaultDestinationByChannel = (channel: SendChannel): string => {
        if (!user) return '';
        return channel === 'EMAIL' ? (user.email ?? '') : (user.phoneNumber ?? '');
    };
    React.useEffect((): void => {
        if (!visible) {
            lastFetchedUuidRef.current = null;
            setLastActiveOtp(null);
            form.resetFields();
            setSendChannel('EMAIL');
            setSendDestination('');
            setSendCooldownUntilMs(0);
        }
    }, [visible]);

    React.useEffect((): void => {
        setLastActiveOtp(null);
    }, [user?.uuid]);

    React.useEffect((): void => {
        if (!visible || !user?.uuid) return;
        if (lastFetchedUuidRef.current === user.uuid) return;
        lastFetchedUuidRef.current = user.uuid;
        fetchRef.current();
    }, [visible, user?.uuid]);

    const { fetch: deleteActiveOtps } = useQueryApiClient({
        request: {
            disableOnMount: true,
            method: 'DELETE',
            url: '/api/v1/auth/otp/' + user?.uuid,
        },
        onSuccess: (): void => {
            lastFetchedUuidRef.current = null;
            fetchRef.current();
        },
    });

    const { appendData: generateOtp } = useQueryApiClient({
        request: {
            disableOnMount: true,
            method: 'POST',
            url: '/api/v1/auth/otp/generate',
        },
        onSuccess: (response: unknown): void => {
            updateLastActiveOtpFromResponse(response);
        },
    });

    const { fetch: sendOtp, isLoading: isSendOtpLoading } = useQueryApiClient({
        request: {
            disableOnMount: true,
            method: 'POST',
            url: '/api/v1/auth/otp/send/' + user?.uuid,
        },
        onSuccess: (response: unknown): void => {
            const sent =
                response && typeof response === 'object' && 'sent' in response
                    ? Boolean((response as { sent: unknown }).sent)
                    : false;

            if (sent) {
                message.success(t('otp.modal.send_success', 'OTP sent.'));
            } else {
                message.error(t('otp.modal.send_failed', 'Failed to send OTP.'));
                setSendCooldownUntilMs(0);
            }

            lastFetchedUuidRef.current = null;
            fetchRef.current();
        },
        onError: (): void => {
            message.error(t('otp.modal.send_failed', 'Failed to send OTP.'));
            setSendCooldownUntilMs(0);
        },
    });

    const handleGeneration = (values: { expiresMinutes?: number }): void => {
        if (user?.uuid) {
            const expiresMinutes = values.expiresMinutes;
            if (typeof expiresMinutes !== 'number') return;
            generateOtp({
                userUuid: user.uuid,
                expiresMinutes: expiresMinutes,
                ...(typeof userOrganizationId === 'number'
                    ? { organizationId: userOrganizationId }
                    : {}),
                ...(userOrganizationName ? { organizationName: userOrganizationName } : {}),
                ...(userOrganizationType ? { organizationType: userOrganizationType } : {}),
            });
        }
    };

    const handleSendOtp = (): void => {
        if (!user?.uuid) return;
        if (sendCooldownUntilMs > Date.now()) return;

        setSendCooldownUntilMs(Date.now() + 30_000);
        const destination = sendDestination.trim();
        const resolvedDestination = destination || getDefaultDestinationByChannel(sendChannel);
        const expiresMinutes = form.getFieldValue('expiresMinutes');

        const payload: SendOtpPayload = {
            channel: sendChannel,
            expiresMinutes: expiresMinutes,
            ...(resolvedDestination ? { destination: resolvedDestination } : {}),
            ...(typeof userOrganizationId === 'number'
                ? { organizationId: userOrganizationId }
                : {}),
            ...(userOrganizationName ? { organizationName: userOrganizationName } : {}),
            ...(userOrganizationType ? { organizationType: userOrganizationType } : {}),
        };

        sendOtp(payload);
    };

    const handleDeleteActiveOtps = (): void => {
        if (!user?.uuid) return;

        Modal.confirm({
            title: t('otp.modal.attention', 'Attention'),
            content: t('otp.modal.delete_all_active_keys', 'Delete all active OTP keys?'),
            okText: t('general.delete'),
            okType: 'danger',
            cancelText: t('general.cancel'),
            onOk: () => {
                deleteActiveOtps({});
            },
        });
    };

    const dbDateFormat = config.DBDateFormat || 'YYYY-MM-DD HH:mm:ss';
    const displayDateFormat = config.defaultFullDateFormat || dbDateFormat;

    const apiDateTimeFormats = [
        'YYYY-MM-DD HH:mm:ss',
        'YYYY-MM-DDTHH:mm:ss[Z]',
        'YYYY-MM-DDTHH:mm:ss.SSSSSS[Z]',
        moment.ISO_8601,
        dbDateFormat,
    ];

    const parseApiDateTime = (value: string | null): moment.Moment | null => {
        if (!value) return null;

        const parsed = moment(value, apiDateTimeFormats, true);
        return parsed.isValid() ? parsed : null;
    };

    const formatApiDateTime = (value: string | null): string => {
        const parsed = parseApiDateTime(value);
        if (!parsed) return '-';

        return parsed.format(displayDateFormat);
    };

    const isLastOtpActive = React.useMemo((): boolean => {
        if (!lastActiveOtp) return false;
        const normalizedUsedAt = lastActiveOtp.used_at?.trim().toLowerCase();
        const isUsed = Boolean(normalizedUsedAt) && normalizedUsedAt !== 'null';
        if (isUsed) return false;

        const expiresAt = parseApiDateTime(lastActiveOtp.expires_at);
        if (!expiresAt) return false;

        return expiresAt.isAfter(moment());
    }, [lastActiveOtp]);

    const PHONE_MIN_DIGITS = 8;
    const trimmedSendDestination = sendDestination.trim();
    const smsDestinationDigits = (
        trimmedSendDestination || getDefaultDestinationByChannel('SMS')
    ).replace(/\D/g, '');
    const isSmsDestinationTooShort =
        sendChannel === 'SMS' && smsDestinationDigits.length < PHONE_MIN_DIGITS;
    const showSmsDestinationError = isSmsDestinationTooShort && trimmedSendDestination.length > 0;

    const isSendCooldownActive = sendCooldownUntilMs > Date.now();
    const sendCooldownSecondsLeft = React.useMemo((): number => {
        if (!isSendCooldownActive) return 0;
        return Math.max(0, Math.ceil((sendCooldownUntilMs - nowMs) / 1000));
    }, [isSendCooldownActive, sendCooldownUntilMs, nowMs]);

    React.useEffect((): (() => void) | void => {
        if (!visible) return;
        if (!isSendCooldownActive) return;

        const intervalId = window.setInterval(() => {
            setNowMs(Date.now());
        }, 250);

        return () => window.clearInterval(intervalId);
    }, [visible, isSendCooldownActive]);

    return (
        <Modal
            className="user-list-modal"
            open={visible}
            title={<TranslatedMessage id="otp.modal.title" />}
            onCancel={handleCancel}
            destroyOnClose
            footer={null}
        >
            <div className="f-16">
                <div className="flex gap-5 ">
                    <strong className="w-35">{t('general.full_name')}: </strong>{' '}
                    <Title title={user?.fullName ?? ''} />
                </div>
                {user?.email ? (
                    <div className="flex gap-5">
                        <strong className="w-35">{t('general.email')}: </strong>{' '}
                        <Title title={user?.email ?? ''} />
                    </div>
                ) : null}
                {user?.phoneNumber ? (
                    <div className="flex gap-5">
                        <strong className="w-35">{t('users.form.phone_number')}: </strong>{' '}
                        <Title title={user?.phoneNumber ?? ''} />
                    </div>
                ) : null}
            </div>
            <Divider />

            <div className="f-16">
                <div className="flex gap-5 ">
                    <strong className="w-35">
                        <TranslatedMessage id="otp.modal.last_active_otp" />
                    </strong>
                    <span>{lastActiveOtp ? '' : '-'}</span>
                </div>

                {lastActiveOtp && (
                    <>
                        <div className="flex gap-5">
                            <strong className="w-35">
                                <TranslatedMessage id="general.status" />
                            </strong>
                            <Tag status={isLastOtpActive ? TagStatus.ACTIVE : TagStatus.INACTIVE} />
                        </div>
                        <div className="flex gap-5">
                            <strong className="w-35">
                                <TranslatedMessage id="general.generated_at" />
                            </strong>
                            <span>{formatApiDateTime(lastActiveOtp.generated_at)}</span>
                        </div>
                        <div className="flex gap-5">
                            <strong className="w-35">
                                <TranslatedMessage id="general.expires_at" />
                            </strong>
                            <span>{formatApiDateTime(lastActiveOtp.expires_at)}</span>
                        </div>
                        <div className="flex gap-5">
                            <strong className="w-35">
                                <TranslatedMessage id="general.used" />
                            </strong>
                            <span>
                                {lastActiveOtp.used_at ? (
                                    <TranslatedMessage id="general.yes" />
                                ) : (
                                    <TranslatedMessage id="general.no" />
                                )}
                            </span>
                        </div>
                        <div className="flex gap-5">
                            <strong className="w-35">
                                <TranslatedMessage id="general.used_at" />
                            </strong>
                            <span>{formatApiDateTime(lastActiveOtp.used_at)}</span>
                        </div>
                        <div className="flex gap-5">
                            <strong className="w-35">
                                <TranslatedMessage id="general.created_by" />
                            </strong>
                            <span>{lastActiveOtp.created_by ?? '-'}</span>
                        </div>
                        {lastActiveOtp?.password ? (
                            <div className="flex gap-5 align-center">
                                <strong className="w-35">
                                    <TranslatedMessage id="general.password" />
                                </strong>
                                <CopyValue value={lastActiveOtp.password} />
                            </div>
                        ) : null}
                    </>
                )}
            </div>

            <Divider />
            <div className="f-16">
                <div className="flex gap-5 align-center">
                    <strong className="w-30">
                        <TranslatedMessage id="otp.modal.send_via" />
                    </strong>
                    <div className="w-70">
                        <Radio.Group
                            value={sendChannel}
                            onChange={(e: RadioChangeEvent) => {
                                setSendChannel(e.target.value as SendChannel);
                                setSendDestination('');
                            }}
                        >
                            <Radio value="EMAIL">
                                <TranslatedMessage id="general.email" />
                            </Radio>
                            <Radio value="SMS">
                                <TranslatedMessage id="general.sms" />
                            </Radio>
                        </Radio.Group>
                    </div>
                </div>
                {sendChannel === 'SMS' && (
                    <Alert
                        type="info"
                        message={<TranslatedMessage id="otp.modal.phone_number_format_info" />}
                    />
                )}
                <div className="flex gap-5 mt-3 align-center">
                    <strong className="w-30">
                        <TranslatedMessage id="general.destination" />
                    </strong>
                    <div className="w-70">
                        <Input
                            className="w-100"
                            value={sendDestination}
                            maxLength={sendChannel === 'EMAIL' ? 100 : 20}
                            status={showSmsDestinationError ? 'error' : undefined}
                            onChange={(e: { target: { value: string } }) =>
                                setSendDestination(e.target.value)
                            }
                            placeholder={
                                getDefaultDestinationByChannel(sendChannel) ||
                                (sendChannel === 'EMAIL'
                                    ? t('general.email')
                                    : t('users.form.phone_number'))
                            }
                        />
                        {showSmsDestinationError ? (
                            <div className="ant-form-item-explain-error">
                                <TranslatedMessage
                                    id="otp.modal.phone_number_min_length"
                                    values={{ min: PHONE_MIN_DIGITS }}
                                />
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
            <Form
                form={form}
                onFinish={handleGeneration}
                initialValues={{ expiresMinutes: 60 }}
                className="mt-3"
            >
                <div className="flex gap-5 align-center">
                    <strong className="w-30">{t('otp.modal.expire_minutes')}</strong>
                    <div className="w-70">
                        <Form.Item name="expiresMinutes" style={{ marginBottom: 0 }}>
                            <AntDInputNumber min={1} className="w-100" />
                        </Form.Item>
                    </div>
                </div>
            </Form>

            <Divider />

            <div className="flex gap-5 mt-3">
                <Button message="general.generate" onClick={form.submit} disabled={!user?.uuid} />
                <Button
                    message={
                        isSendCooldownActive
                            ? `${t('general.sent')} (${sendCooldownSecondsLeft}s)`
                            : 'general.send'
                    }
                    type="outline"
                    onClick={handleSendOtp}
                    loading={isSendOtpLoading}
                    disabled={
                        !user?.uuid ||
                        isSendCooldownActive ||
                        isSmsDestinationTooShort
                    }
                />
                <Button
                    message="general.delete"
                    type="outline"
                    disabled={!user?.uuid || !isLastOtpActive}
                    danger
                    onClick={handleDeleteActiveOtps}
                />
            </div>
        </Modal>
    );
};

export default connect(mapStateToProps)(OTPModal);
